import { queryClient } from '@/lib/query-client'
import { useTokenStore } from '@/lib/store'
import { trackEvent } from '@/lib/telemetry'
import { useEffect } from 'react'

const CHECK_INTERVAL_MS = 5 * 60_000
const TICK_MS = 30_000
// How long the user must be gone before the tab reloads under them.
const HIDDEN_FOR_MS = 60_000
const IDLE_FOR_MS = 10 * 60_000
const RELOADED_TO_KEY = 'cpq-update-reloaded-to'

const holds = new Set<symbol>()

/** Holds automatic updates until the returned release runs. */
export function holdAppUpdates() {
  const hold = Symbol('unsaved')
  holds.add(hold)
  return () => void holds.delete(hold)
}

/**
 * Holds automatic updates while `unsaved` is true: work kept only in component
 * state that a reload would drop (a drawn signature).
 */
export function useHoldAppUpdates(unsaved: boolean) {
  useEffect(() => (unsaved ? holdAppUpdates() : undefined), [unsaved])
}

/**
 * Nothing on screen can be lost: no save in flight, no edits waiting behind
 * the session-expired banner, no unsaved form holding updates, no dialog open,
 * no field mid-edit. A field counts only while the window has focus; leaving
 * the window blurs it, which commits the edit as a save.
 */
export function isSafeToReload() {
  if (holds.size > 0) return false
  if (queryClient.isMutating() > 0) return false
  if (useTokenStore.getState().sessionExpired) return false
  if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return false
  const active = document.activeElement
  const editing =
    active instanceof HTMLElement && (active.isContentEditable || active.matches('input, textarea, select'))
  return !(editing && document.hasFocus())
}

type AppUpdateOptions = {
  /** This bundle's build, compared with the deployed `version.json`. */
  buildId: string
  isSafeToReload: () => boolean
  reload?: () => void
}

/**
 * Keeps a long-open tab on the latest deploy with no action from the user.
 * Polls `version.json` (written per build, served no-cache); once a newer build
 * is live, reloads the first time the user has left the tab for a minute or
 * been idle for ten, with nothing to lose. One reload per build, so an edge
 * still serving the old page can't loop.
 */
export function startAppUpdates({
  buildId,
  isSafeToReload,
  reload = () => window.location.reload(),
}: AppUpdateOptions) {
  let latest: string | null = null
  let lastActiveAt = Date.now()

  const check = async () => {
    if (!navigator.onLine) return
    try {
      const response = await fetch('/version.json', { cache: 'no-store' })
      if (!response.ok) return
      const deployed = ((await response.json()) as { buildId?: unknown } | null)?.buildId
      if (typeof deployed !== 'string' || deployed === buildId || deployed === latest) return
      latest = deployed
      trackEvent('app_update_available', { from: buildId, to: deployed })
    } catch {
      // Offline or mid-deploy: the next check tries again.
    }
  }

  const reloadIfAway = () => {
    if (!latest) return
    const awayFor = Date.now() - lastActiveAt
    if (awayFor < (document.visibilityState === 'hidden' ? HIDDEN_FOR_MS : IDLE_FOR_MS)) return
    if (!isSafeToReload()) return
    try {
      if (sessionStorage.getItem(RELOADED_TO_KEY) === latest) return
      sessionStorage.setItem(RELOADED_TO_KEY, latest)
    } catch {
      return // can't record the attempt → don't risk a reload loop
    }
    trackEvent('app_update_reload', { from: buildId, to: latest })
    reload()
  }

  const markActive = () => {
    lastActiveAt = Date.now()
  }
  for (const type of ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart']) {
    window.addEventListener(type, markActive, { capture: true, passive: true })
  }
  document.addEventListener('visibilitychange', () => {
    markActive()
    if (document.visibilityState === 'visible') void check()
  })
  void check()
  setInterval(check, CHECK_INTERVAL_MS)
  setInterval(reloadIfAway, TICK_MS)
}
